import React, { useState, useEffect, useRef } from 'react';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaEnvelope, FaEye, FaCode, FaSave, FaUndo, FaTag,
  FaSearch, FaShieldAlt, FaUsers, FaCreditCard, FaClipboardCheck, FaBell
} from 'react-icons/fa';
import { getApiEndpoint } from '../../../config/api';
import './EmailTemplatesPage.css';

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const CATEGORIES = [
  { key: 'all',      label: 'All',          icon: FaEnvelope },
  { key: 'auth',     label: 'Auth',         icon: FaEnvelope },
  { key: 'security', label: 'Security',     icon: FaShieldAlt },
  { key: 'team',     label: 'Team',         icon: FaUsers },
  { key: 'billing',  label: 'Billing',      icon: FaCreditCard },
  { key: 'ops',      label: 'Operational',  icon: FaClipboardCheck },
  { key: 'general',  label: 'General',      icon: FaBell },
];

const CATEGORY_COLORS = {
  auth:     { accent: '#38bdf8', dim: 'rgba(56,189,248,0.15)',  text: '#38bdf8' },
  security: { accent: '#f87171', dim: 'rgba(248,113,113,0.15)', text: '#f87171' },
  team:     { accent: '#34d399', dim: 'rgba(52,211,153,0.15)',  text: '#34d399' },
  billing:  { accent: '#fbbf24', dim: 'rgba(251,191,36,0.15)',  text: '#fbbf24' },
  ops:      { accent: '#fb923c', dim: 'rgba(251,146,60,0.15)',  text: '#fb923c' },
  general:  { accent: '#a78bfa', dim: 'rgba(167,139,250,0.15)', text: '#a78bfa' },
};

export default function EmailTemplatesPage() {
  const [templates, setTemplates]   = useState([]);
  const [htmlMap, setHtmlMap]       = useState({});   // { filename: html }
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null); // template object
  const [mode, setMode]             = useState('preview');
  const [editHtml, setEditHtml]     = useState('');
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [error, setError]           = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch]         = useState('');
  const previewRef                  = useRef(null);

  // ── Load template list + all HTML in parallel ────────────────────
  useEffect(() => {
    fetch(getApiEndpoint('/api/cloud-staff/email-templates'), { headers: authHeaders() })
      .then(r => r.json())
      .then(async data => {
        const list = data.templates || [];
        setTemplates(list);
        // Pre-fetch all HTML for gallery previews
        const results = await Promise.allSettled(
          list.map(t =>
            fetch(getApiEndpoint(`/api/cloud-staff/email-templates/${t.filename}`), { headers: authHeaders() })
              .then(r => r.json())
              .then(d => ({ filename: t.filename, html: d.html }))
          )
        );
        const map = {};
        results.forEach(r => { if (r.status === 'fulfilled') map[r.value.filename] = r.value.html; });
        setHtmlMap(map);
        setLoading(false);
      })
      .catch(() => { setError('Failed to load templates'); setLoading(false); });
  }, []);

  // ── Select template ──────────────────────────────────────────────
  const handleSelect = (tpl) => {
    setSelected(tpl);
    setEditHtml(htmlMap[tpl.filename] || '');
    setMode('preview');
    setSaved(false);
    if (previewRef.current) previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Save ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/email-templates/${selected.filename}`), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ html: editHtml }),
      });
      if (!res.ok) throw new Error('save failed');
      setHtmlMap(prev => ({ ...prev, [selected.filename]: editHtml }));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { setError('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDiscard = () => { setEditHtml(htmlMap[selected.filename] || ''); setMode('preview'); };
  const isDirty = selected && editHtml !== (htmlMap[selected.filename] || '');

  // ── Filter templates ─────────────────────────────────────────────
  const visible = templates.filter(t => {
    const matchCat = activeCategory === 'all' || t.category === activeCategory;
    const matchSearch = !search || t.label.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const col = selected ? CATEGORY_COLORS[selected.category] || CATEGORY_COLORS.general : null;

  return (
    <div className="etp-shell">
      <CloudStaffSidebar active="email-templates" />

      <main className="etp-main">

        {/* ── Top header ── */}
        <div className="etp-header">
          <div className="etp-header-left">
            <div className="etp-header-icon"><FaEnvelope /></div>
            <div>
              <h1 className="etp-title">Email Studio</h1>
              <p className="etp-subtitle">{templates.length} templates · select one to preview or edit</p>
            </div>
          </div>
          {selected && mode === 'edit' && (
            <div className="etp-header-actions">
              <button className="etp-btn etp-btn-ghost" onClick={handleDiscard} disabled={saving}>
                <FaUndo /> Discard
              </button>
              <button
                className={`etp-btn etp-btn-primary ${saving ? 'is-loading' : ''} ${saved ? 'is-saved' : ''}`}
                onClick={handleSave}
                disabled={saving || !isDirty}
              >
                <FaSave /> {saving ? 'Saving…' : saved ? 'Saved!' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="etp-error-banner">{error}<button onClick={() => setError(null)}>✕</button></div>
        )}

        <div className="etp-layout">

          {/* ══ LEFT — gallery panel ══ */}
          <aside className="etp-gallery-panel">

            {/* Search */}
            <div className="etp-search-wrap">
              <FaSearch className="etp-search-icon" />
              <input
                className="etp-search"
                placeholder="Search templates…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {/* Category filter */}
            <div className="etp-cat-filters">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.key}
                  className={`etp-cat-btn ${activeCategory === cat.key ? 'active' : ''}`}
                  onClick={() => setActiveCategory(cat.key)}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Template cards */}
            <div className="etp-gallery-list">
              {loading && (
                <div className="etp-gallery-loading">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="etp-card-skeleton" style={{ animationDelay: `${i * 0.08}s` }} />
                  ))}
                </div>
              )}

              {!loading && visible.length === 0 && (
                <div className="etp-gallery-empty">No templates match.</div>
              )}

              {!loading && visible.map(tpl => {
                const c = CATEGORY_COLORS[tpl.category] || CATEGORY_COLORS.general;
                const html = htmlMap[tpl.filename] || '';
                const isActive = selected?.filename === tpl.filename;
                return (
                  <button
                    key={tpl.filename}
                    className={`etp-card ${isActive ? 'active' : ''}`}
                    onClick={() => handleSelect(tpl)}
                    style={isActive ? { '--card-accent': c.accent, '--card-dim': c.dim } : {}}
                  >
                    {/* Mini iframe preview */}
                    <div className="etp-card-preview">
                      {html ? (
                        <iframe
                          className="etp-card-iframe"
                          srcDoc={html}
                          title={tpl.label}
                          sandbox="allow-same-origin"
                          tabIndex={-1}
                        />
                      ) : (
                        <div className="etp-card-preview-loading" />
                      )}
                    </div>

                    {/* Card footer */}
                    <div className="etp-card-footer">
                      <span
                        className="etp-card-badge"
                        style={{ background: c.dim, color: c.text }}
                      >
                        {tpl.category}
                      </span>
                      <span className="etp-card-name">{tpl.label}</span>
                      <span className="etp-card-vars">{tpl.variables.length} vars</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* ══ RIGHT — detail panel ══ */}
          <div className="etp-detail-panel" ref={previewRef}>
            {!selected && (
              <div className="etp-empty-state">
                <FaEnvelope className="etp-empty-icon" />
                <p>Select a template to preview</p>
              </div>
            )}

            {selected && (
              <>
                {/* Detail header */}
                <div className="etp-detail-header" style={{ '--col-accent': col.accent, '--col-dim': col.dim }}>
                  <div className="etp-detail-title-row">
                    <div>
                      <span className="etp-detail-badge" style={{ background: col.dim, color: col.accent }}>
                        {selected.category}
                      </span>
                      <h2 className="etp-detail-title">{selected.label}</h2>
                      <p className="etp-detail-file">{selected.filename}</p>
                    </div>
                    <div className="etp-tab-group">
                      <button className={`etp-tab ${mode === 'preview' ? 'active' : ''}`} onClick={() => setMode('preview')}>
                        <FaEye /> Preview
                      </button>
                      <button className={`etp-tab ${mode === 'edit' ? 'active' : ''}`} onClick={() => setMode('edit')}>
                        <FaCode /> Edit {isDirty && <span className="etp-dirty-dot" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                {mode === 'preview' && (
                  <div className="etp-preview-wrap">
                    <iframe
                      className="etp-preview-frame"
                      srcDoc={htmlMap[selected.filename] || ''}
                      title={selected.label}
                      sandbox="allow-same-origin"
                    />
                  </div>
                )}

                {/* Editor */}
                {mode === 'edit' && (
                  <div className="etp-editor-wrap">
                    <textarea
                      className="etp-editor"
                      value={editHtml}
                      onChange={e => setEditHtml(e.target.value)}
                      spellCheck={false}
                    />
                  </div>
                )}

                {/* Variables */}
                <div className="etp-vars-panel">
                  <div className="etp-vars-label"><FaTag /> Variables</div>
                  <div className="etp-vars-list">
                    {selected.variables.map(v => (
                      <span key={v} className="etp-var-chip" style={{ '--chip-color': col.accent }}>
                        {`{{${v}}}`}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
